import { useState, useEffect } from "react";
import { useAppDispatch } from "../../app/hook";
import { useParams, useNavigate } from "react-router-dom";
import {
  formatDate,
  formatDateHyphen,
  formatYearDate,
} from "../../utils/format";
import {
  SpaceBetween,
  Form,
  Container,
  Header,
  Badge,
} from "@cloudscape-design/components-themed";
import { Messages, getMessage } from "../../features/error/messages";
import { addNotification } from "../../features/error/notifications-slice";
import { LABELS, getIndustryConversionsItems } from "./constants";
import {
  COMMON_LABELS,
  ApprovalStatusPattern,
  ActionPattern,
  DivisionDict,
  getDivisionDict,
  getFirstDivisionDict,
} from "../default-constants";
import { Buttons } from "./Buttons";
import { HelpPanel } from "./HelpPanel";
import { FormBlock, Comment } from "./Forms";
import { openHelpPanel } from "../layouts/help-panel-slice";
import { isNull, isLengthValid } from "../../utils/inputValidation";
import {
  useGetIndustryAfterConversionCodeQuery,
  usePostIndustryConversionsMutation,
  useGetApprovedIndustryConversionQuery,
  usePutIndustryConversionMutation,
  useDeleteIndustryConversionMutation,
} from "../../features/api";
import {
  IndustryConversion,
  CommercialIndustries,
  IndustryConversionsRequest,
  IndustryConversionsUpdate,
} from "../../features/api-interfaces";

export const IndustryConversionForm = (props: any) => {
  const dispatch = useAppDispatch();
  const navigator = useNavigate();
  const { no } = useParams();
  const [postIndustryConversions] = usePostIndustryConversionsMutation();
  const [putIndustryConversion] = usePutIndustryConversionMutation();
  const [deleteIndustryConversion] = useDeleteIndustryConversionMutation();
  // 各入力項目
  const [applicationStartDate, setApplicationStartDate] = useState("");
  const [conversionRuleNo, setConversionRuleNo] = useState<string>("");
  const [ruleNo, setRuleNo] = useState<number>();
  const [beforeConversion, setBeforeConversion] = useState("");
  const [afterConversion, setAfterConversion] = useState<DivisionDict>({
    label: "",
    value: "",
  });
  const [comment, setComment] = useState("");
  const [updateAt, setUpdateAt] = useState("");
  const [updateBy, setUpdateBy] = useState("");
  const [actionAt, setActionAt] = useState("");
  const [actionBy, setActionBy] = useState("");
  const [actionComment, setActionComment] = useState("");
  const [commentErrorMsg, setCommentErrorMsg] = useState("");

  //入力チェックエラーメッセージ
  const [conversionStartDataErrorMsg, setConversionStartDataErrorMsg] =
    useState("");
  const [beforeConversionErrorMsg, setBeforeConversionErrorMsg] = useState("");
  const [afterConversionErrorMsg, setAfterConversionErrorMsg] = useState("");

  // データ管理用
  const [branchNo, setBranchNo] = useState<number>();
  const [approvalStatus, setApprovalStatus] = useState<number>(0);
  const [readOnly, setReadOnly] = useState(false);
  const [approvalFlowNo, setApprovalFlowNo] = useState<number>();

  //各API呼び出し
  //業態マスタテーブルから変換後業態コードのリストを表示
  const IndustryConversionsData = useGetIndustryAfterConversionCodeQuery();
  const afterConversionItems =
    IndustryConversionsData?.data as CommercialIndustries[];
  const afterConversionFormItems =
    getIndustryConversionsItems(afterConversionItems);

  // データをセット
  const registeredData: IndustryConversion = props.registeredData;

  //情報取得
  useEffect(() => {
    setAfterConversion(getFirstDivisionDict(afterConversionFormItems));
    // 登録済みのデータを表示する
    if (registeredData !== undefined) {
      const conversionRuleNo = no as string;
      setConversionRuleNo(conversionRuleNo);
      setApplicationStartDate(
        formatDateHyphen(registeredData.applicationStartDate)
      );
      setRuleNo(registeredData.ruleNo);
      setBeforeConversion(registeredData.conversionFromIndustryName);
      setAfterConversion(
        getDivisionDict(
          afterConversionFormItems,
          registeredData.conversionAfterIndustryCode
        )
      );
      setBranchNo(registeredData.branchNo);
      setUpdateAt(formatYearDate(registeredData.updatedAt));
      setUpdateBy(registeredData.updatedBy);
      setActionAt(formatYearDate(registeredData.actionAt));
      setActionBy(registeredData.actionBy);
      setApprovalStatus(registeredData.approvalStatus);
      setActionComment(registeredData.actionComment);
      setApprovalFlowNo(registeredData.approvalFlowNo);
      switchReadOnly(registeredData.approvalStatus);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [afterConversionItems, registeredData]);

  //登録データ整理
  const getIndustryConversionsRequest = (
    type: string,
    approvalAction: number
  ): IndustryConversionsRequest => {
    if (type === "register") {
      setBranchNo(0);
    }
    const industryConversionsRequest: IndustryConversionsRequest = {
      branchNo: branchNo,
      conversionFromIndustryName: beforeConversion,
      conversionAfterIndustryCode: afterConversion.value,
      applicationStartDate: formatDate(applicationStartDate),
      approvalStatus: approvalStatus,
      approvalAction: approvalAction,
      actionComment: comment,
    };
    if (type === "register") {
      return {
        ...industryConversionsRequest,
      };
    } else if (type === "registerUpdate") {
      return {
        ...industryConversionsRequest,
        ruleNo: ruleNo,
        branchNo: branchNo,
        approvalStatus: 0,
      };
    } else {
      return {
        ...industryConversionsRequest,
        ruleNo: ruleNo,
        branchNo: branchNo,
        approvalFlowNo: approvalFlowNo,
      };
    }
  };

  //商用反映済み業態変換情報取得API呼び出し;
  let registeredRuleNo: number = 0;
  if (registeredData) {
    registeredRuleNo = registeredData.ruleNo;
  }
  const { data: data2, refetch: commercialDataRefetch } =
    useGetApprovedIndustryConversionQuery(registeredRuleNo);
  const commercialData = data2 as IndustryConversion;

  // 業態変換情報登録API呼び出し
  const register = (approvalAction: number) => {
    const industryConversionsRequest = getIndustryConversionsRequest(
      "register",
      approvalAction
    );
    postIndustryConversions(industryConversionsRequest).then((result: any) => {
      if (!("error" in result) && !isNull(result.data.conversionRuleNo)) {
        navigator(
          `/industry-conversion/registry/${result.data.conversionRuleNo}`
        );
      }
    });
  };

  // 業態変換情報更新API呼び出し
  const update = (approvalAction: number, url: string) => {
    const industryConversionsRequest = getIndustryConversionsRequest(
      "update",
      approvalAction
    );
    const data: IndustryConversionsUpdate = {
      conversionRuleNo: conversionRuleNo,
      data: industryConversionsRequest,
    };
    putIndustryConversion(data).then((result: any) => {
      if (!("error" in result)) {
        props.refetch();
        commercialDataRefetch();
        navigator(url);
      }
    });
  };

  // 業態変換情報登録API呼び出し(バージョン更新時)
  const registerUpdate = (approvalAction: number) => {
    const industryConversionsRequest = getIndustryConversionsRequest(
      "registerUpdate",
      approvalAction
    );
    postIndustryConversions(industryConversionsRequest).then((result: any) => {
      if (!("error" in result)) {
        navigator(
          `/industry-conversion/registry/${result.data.conversionRuleNo}`
        );
      }
    });
  };

  // 業態変換情報削除API呼び出し
  const deleteSave = (approvalAction: number) => {
    const industryConversionsRequest = getIndustryConversionsRequest(
      "deleteSave",
      approvalAction
    );
    const data: IndustryConversionsUpdate = {
      conversionRuleNo: conversionRuleNo,
      data: industryConversionsRequest,
    };
    deleteIndustryConversion(data).then((result: any) => {
      if (!("error" in result)) {
        navigator("/industry-conversions");
      }
    });
  };

  // 入力制御
  const switchReadOnly = (approvalStatus: number) => {
    if (
      approvalStatus === ApprovalStatusPattern.beforeApproval.statusCode ||
      approvalStatus ===
        ApprovalStatusPattern.commercialApplication.statusCode ||
      approvalStatus === 0
    ) {
      setReadOnly(false);
    } else {
      setReadOnly(true);
    }
  };
  //各入力情報処理
  // 適用開始日入力
  const onChangApplicationStartDate = (applicationStartDate: string) => {
    setApplicationStartDate(applicationStartDate);
    checkApplicationStartDate(applicationStartDate);
  };

  // 変換元業態名入力
  const onChangeBeforeConversion = (beforeConversion: string) => {
    setBeforeConversion(beforeConversion);
    checkBeforeConversion(beforeConversion);
  };

  // 変換後業態コード入力
  const onChangeAfterConversion = (afterConversion: DivisionDict) => {
    setAfterConversion(afterConversion);
    checkAfterConversion(afterConversion);
  };

  // コメント入力
  const onChangeComment = (comment: string) => {
    setComment(comment);
    checkComment(comment);
  };

  // バリデーションチェック
  const checkInput = (): boolean => {
    let flag = true;
    const checks = [
      checkApplicationStartDate(applicationStartDate),
      checkBeforeConversion(beforeConversion),
    ];
    checks.forEach(function (value) {
      if (value === false) {
        flag = false;
      }
    });
    return flag;
  };

  // 適用開始日バリデーションチェック
  const checkApplicationStartDate = (applicationStartDate: string): boolean => {
    if (isNull(applicationStartDate)) {
      setConversionStartDataErrorMsg(
        getMessage("KGRERR023", LABELS.START_DATE).label
      );
      return false;
    } else {
      setConversionStartDataErrorMsg("");
      return true;
    }
  };

  // 変換元業態名バリデーションチェック
  const checkBeforeConversion = (beforeConversion: string): boolean => {
    if (isNull(beforeConversion)) {
      setBeforeConversionErrorMsg(
        getMessage("KGRERR023", LABELS.BEFORE_CONVERSION).label
      );
      return false;
    } else if (!isLengthValid(beforeConversion, 100)) {
      setBeforeConversionErrorMsg(
        getMessage("KGRERR024", LABELS.BEFORE_CONVERSION, 100).label
      );
      return false;
    } else {
      setBeforeConversionErrorMsg("");
      return true;
    }
  };

  // 変換後業態コードバリデーションチェック
  const checkAfterConversion = (afterConversion: DivisionDict): boolean => {
    if (isNull(afterConversion.value)) {
      setAfterConversionErrorMsg(
        getMessage("KGRERR023", LABELS.AFTER_CONVERSION).label
      );
      return false;
    } else {
      setAfterConversionErrorMsg("");
      return true;
    }
  };
  // コメントバリデーションチェック
  const checkComment = (comment: string): boolean => {
    if (!isLengthValid(comment, 2000)) {
      setCommentErrorMsg(getMessage("KGRERR024", LABELS.COMMENT, 2000).label);
      return false;
    } else {
      setCommentErrorMsg("");
      return true;
    }
  };

  // 差し戻し時のバリデーションチェック
  const checkWhenSendBack = (): boolean => {
    if (isNull(comment)) {
      const messageId = "KGRERR029" as keyof Messages;
      // アラート処理
      dispatch(
        addNotification({
          msgId: messageId,
          msgArgs: [COMMON_LABELS.BTNS.SEND_BACK, LABELS.COMMENT],
        })
      );
      return false;
    } else {
      return true;
    }
  };

  // ボタン押下時の処理
  // 一時保存ボタン
  const onClickTemporarilySaveBtn = () => {
    if (checkInput()) {
      if (conversionRuleNo === "") {
        // 初期登録時
        register(ActionPattern.temporarilySave.statusCode);
      } else if (
        approvalStatus ===
          ApprovalStatusPattern.commercialApplication.statusCode &&
        ruleNo !== undefined
      ) {
        // バージョン更新時
        registerUpdate(ActionPattern.temporarilySave.statusCode);
      } else {
        // 更新時
        update(
          ActionPattern.temporarilySave.statusCode,
          `/industry-conversions/`
        );
      }
    }
  };

  // 承認依頼ボタン
  const onClickApprovalRequestBtn = () => {
    if (checkInput()) {
      update(
        ActionPattern.approvalRequest.statusCode,
        `/industry-conversions/`
      );
    }
  };

  // 一時保存削除ボタン
  const onClickDeleteTemporarilySaveBtn = () => {
    deleteSave(ActionPattern.delete.statusCode);
  };

  // 承認ボタン
  const onClickApprovalBtn = () => {
    update(ActionPattern.approval.statusCode, `/industry-conversions/`);
  };

  // 差戻ボタン
  const onClickSendBackBtn = () => {
    if (checkWhenSendBack()) {
      update(ActionPattern.sendBack.statusCode, `/industry-conversions/`);
    }
  };

  // 引戻ボタン（承認前）
  const onClickPullBackBtn = () => {
    update(ActionPattern.pullBack.statusCode, `/industry-conversions/`);
  };

  // 引戻ボタン（承認済み）
  const onClickApprovedPullBackBtn = () => {
    update(ActionPattern.approvedPullBack.statusCode, `/industry-conversions/`);
  };

  const onClickCommercialApplicationInfo = function () {
    //　商用反映済情報
    dispatch(
      openHelpPanel({
        header: <div>{LABELS.INDUSTRY_CONVERSIONS_INFO}</div>,
        content: (
          <HelpPanel
            conversionRuleNo={commercialData.ruleNo}
            commercialData={commercialData}
            afterConversionFormItems={afterConversionFormItems}
          ></HelpPanel>
        ),
      })
    );
  };

  return (
    <form onSubmit={(e) => e.preventDefault()}>
      <Form
        actions={
          <Buttons
            approvalStatus={approvalStatus}
            onClickTemporarilySaveBtn={onClickTemporarilySaveBtn}
            onClickApprovalRequestBtn={onClickApprovalRequestBtn}
            onClickDeleteTemporarilySaveBtn={onClickDeleteTemporarilySaveBtn}
            onClickApprovalBtn={onClickApprovalBtn}
            onClickSendBackBtn={onClickSendBackBtn}
            onClickPullBackBtn={onClickPullBackBtn}
            onClickApprovedPullBackBtn={onClickApprovedPullBackBtn}
          />
        }
      >
        <SpaceBetween direction="vertical" size="l">
          <Container
            header={
              <Header
                variant="h3"
                actions={
                  <SpaceBetween direction="horizontal" size="xs">
                    {registeredData ? (
                      <Badge color="green">
                        {LABELS.LAST_UPDATER}
                        {updateBy}/{LABELS.LAST_UPDATING_TIME}
                        {updateAt}
                      </Badge>
                    ) : (
                      <></>
                    )}
                    {registeredData &&
                    (registeredData.approvalStatus ===
                      ApprovalStatusPattern.afterApproval.statusCode ||
                      registeredData.approvalStatus ===
                        ApprovalStatusPattern.commercialApplication
                          .statusCode) ? (
                      <Badge color="green">
                        {LABELS.LAST_APPROVAL_DATE}
                        {actionBy}/{LABELS.LAST_AUTHORIZER}
                        {actionAt}
                      </Badge>
                    ) : (
                      <></>
                    )}
                  </SpaceBetween>
                }
              ></Header>
            }
          >
            <FormBlock
              conversionStartDataErrorMsg={conversionStartDataErrorMsg}
              onChangApplicationStartDate={onChangApplicationStartDate}
              applicationStartDate={applicationStartDate}
              setRuleNo={setRuleNo}
              ruleNo={ruleNo}
              beforeConversionErrorMsg={beforeConversionErrorMsg}
              onChangeBeforeConversion={onChangeBeforeConversion}
              beforeConversion={beforeConversion}
              afterConversion={afterConversion}
              onChangeAfterConversion={onChangeAfterConversion}
              readOnly={readOnly}
              afterConversionItems={afterConversionFormItems}
              afterConversionErrorMsg={afterConversionErrorMsg}
              onClickCommercialApplicationInfo={
                onClickCommercialApplicationInfo
              }
              commercialData={commercialData}
            />
          </Container>
          {approvalStatus !==
          ApprovalStatusPattern.commercialApplication.statusCode ? (
            <Comment
              actionComment={actionComment}
              comment={comment}
              commentErrorMsg={commentErrorMsg}
              onChangeComment={onChangeComment}
            />
          ) : undefined}
        </SpaceBetween>
      </Form>
    </form>
  );
};
